/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * BROWSE-47 — Store resolution waterfall + delivery-ZIP reconciliation.
 *
 * Owns: cookie names, TTLs, URL-param names, default-zone fallback, the
 * waterfall source enum, and the reconciliation flash codes. Anything that
 * needs to be shared between the middleware, action routes, the toast
 * component, and tests imports from here.
 */

export const ASH_STORE_COOKIE_NAME = 'ash-store';
export const ASH_DELIVERY_ZIP_COOKIE_NAME = 'ash-delivery-zip';

export const ASH_STORE_COOKIE_EXPIRY_SECONDS = 180 * 24 * 60 * 60;
export const ASH_DELIVERY_ZIP_COOKIE_EXPIRY_SECONDS = 180 * 24 * 60 * 60;

/** Header set by middleware on responses where reconciliation produced a flash message. */
export const ZIP_FLASH_HEADER = 'x-zip-flash';

/**
 * Request header carrying the viewer's postal code, forwarded by Managed
 * Runtime's eCDN (CloudFront). Read for the IP-geolocation waterfall step
 * (legacy `ZipManager.getGeolocatedZipCode`). MRT must be configured to forward
 * the CloudFront viewer geolocation headers for this to be populated; when it is
 * absent the geo step is skipped and the waterfall falls through to the default.
 */
export const GEO_POSTAL_CODE_HEADER = 'cloudfront-viewer-postal-code';

/**
 * Display label for a present-but-unmapped ZIP (legacy NON_MAPPED_ZIP_PRICE_ZONE).
 * Not a price-book selector — the AFHSDPG assignment is disabled, so an unmapped
 * shopper prices from the site-default books (legacy parity).
 */
export const DEFAULT_PRICE_ZONE = 'AFHSDPG';

/**
 * Price zone forced by the Merkle / Google-Shopping marketing deep link
 * (`?mrkgcl=…&product_id=…`) when the shopper has no ZIP or region yet.
 * Legacy parity: org_ashcomm Product.js `show()` activates `usd-AFHSPGZ1-*`
 * ("Zone 1") for a Merkle landing with no zip/region. Note this is a PRICE ZONE
 * only — legacy assigns NO physical store for the bare-Merkle case.
 */
export const MERKLE_PRICE_ZONE = 'AFHSPGZ1';

/** Hard-coded default store ID for the AFHSDPG fallback path. */
export const DEFAULT_STORE_ID = 'AFHSDPG';

/**
 * URL parameter names that seed the waterfall on inbound requests.
 * Verbatim legacy names — marketing/email/store-locator deep links use these.
 *
 * `store_code`, `region_id`, and the Merkle pair (`mrkgcl` + `product_id`) are
 * the verified legacy deep-link params (org_ashcomm Product.js / ProductUtils
 * `verifyUrlHasMerkleParameter`). `postal_code` is NOT a legacy param — it is a
 * net-new Storefront Next deep-link entry point added for parity with the
 * customer flow diagram.
 */
export const URL_PARAM_STORE_CODE = 'store_code';
export const URL_PARAM_POSTAL_CODE = 'postal_code';
export const URL_PARAM_REGION_ID = 'region_id';
/** Merkle / Google-Shopping marketing param. Only honoured when paired with `product_id`. */
export const URL_PARAM_MERKLE = 'mrkgcl';
export const URL_PARAM_PRODUCT_ID = 'product_id';

/** US ZIP code regex — 5 digits or 5+4. */
export const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;

/**
 * Source attribution on the resolved store. Tells the reconciler whether the
 * resolution was deep-linked (URL params), persisted (cookie), profile-driven,
 * geolocated, or hard-coded default. Geolocated stores are NOT persisted to
 * the long-lived ash-store cookie — they recompute every visit.
 */
export const STORE_SOURCE = {
    STORE_CODE: 'store_code',
    POSTAL_CODE: 'postal_code',
    REGION_ID: 'region_id',
    MERKLE: 'merkle',
    COOKIE: 'cookie',
    PROFILE: 'profile',
    GEO: 'geo',
    DEFAULT: 'default',
    MANUAL: 'manual',
} as const;
export type StoreSource = (typeof STORE_SOURCE)[keyof typeof STORE_SOURCE];

/**
 * Flash message codes set on the response by the reconciler. Consumed by
 * <ZipUpdateToast> via the x-zip-flash response header.
 *
 * Per the customer flow diagram (the authoritative source), the notification
 * fires ONLY when a shopper's MANUALLY-entered delivery ZIP is overwritten
 * because the newly resolved store is in a DIFFERENT price zone (Step C, manual
 * branch). Same-zone resolutions retain the prior ZIP silently, and an
 * auto-derived ZIP overwritten cross-zone updates silently.
 */
export const ZIP_FLASH = {
    ZONE_UPDATED: 'zone-updated',
} as const;
export type ZipFlashCode = (typeof ZIP_FLASH)[keyof typeof ZIP_FLASH];
